'use client';

import React, { useState } from 'react';
import { Plus, Minus } from 'lucide-react';

const faqs = [
  {
    id: 'faq-001',
    q: 'Are REUSA qualifications recognised by employers in South Africa?',
    a: 'Yes. All REUSA programmes are NQF-accredited and registered with SAQA, making them nationally recognised qualifications. Additionally, our industry partnerships with firms like Pam Golding, JLL, Absa, and Growthpoint mean that REUSA graduates are actively sought after by top employers.',
  },
  {
    id: 'faq-002',
    q: 'How does the AI-powered learning work in practice?',
    a: 'Our AI system tracks your study behaviour, assessment performance, and knowledge retention in real time. It then adjusts the difficulty and sequence of content, suggests supplementary resources when you struggle with concepts, and provides a 24/7 conversational study assistant that can explain any course material in plain language.',
  },
  {
    id: 'faq-003',
    q: 'Can I study while working full-time?',
    a: 'Absolutely — REUSA was designed for working professionals. All programmes are available in self-paced online format. Live sessions and masterclasses are recorded and accessible on-demand. Most students invest 8–12 hours per week, and our AI assistant adapts to your available study time.',
  },
  {
    id: 'faq-004',
    q: 'What payment options are available?',
    a: 'We offer flexible payment structures: full upfront payment (with a 5% discount), monthly installments over 6 or 12 months, and corporate billing for employer-sponsored students. We accept EFT, major credit cards, and select buy-now-pay-later options. SETA funding may also be available for qualifying students.',
  },
  {
    id: 'faq-005',
    q: 'Are there scholarships or bursaries available?',
    a: 'Yes. REUSA offers the REUSA Excellence Scholarship for top academic performers, the Emerging Leaders Bursary for students from previously disadvantaged backgrounds, and the Women in Property Scholarship. Corporate bursaries are also available through our employer partnerships. Visit the Scholarships section to apply.',
  },
  {
    id: 'faq-006',
    q: 'Can international students enrol at REUSA?',
    a: 'Yes. REUSA has students in 47 countries. International students study in English and pay in ZAR or USD. Some programmes include South Africa-specific regulatory content, but the investment, development, and AI programmes are fully internationally applicable. We also offer pan-African real estate content.',
  },
  {
    id: 'faq-007',
    q: 'What is the application process?',
    a: 'Applications are completed online through our portal. You will submit your personal details, educational background, and a brief motivation statement. Most programmes have open rolling admissions with quarterly intake dates. Competitive programmes like the Executive MBA-style offering require an interview. Decisions are typically communicated within 5 business days.',
  },
];

export default function FAQSection() {
  const [openId, setOpenId] = useState<string | null>('faq-001');

  return (
    <section className="section-pad-lg bg-background" id="faq">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
          {/* Left */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <div className="divider-gold" />
              <span className="label-caps">Frequently Asked Questions</span>
            </div>
            <h2 className="font-serif text-section-title text-foreground mb-4">
              Everything You
              <br />
              <span className="italic">Need to Know</span>
            </h2>
            <p className="font-sans text-base text-muted-foreground leading-relaxed mb-8">
              Can&apos;t find your answer? Our admissions team is available Monday–Friday, 08:00–18:00 SAST.
            </p>
            <div className="space-y-3">
              <button className="btn-primary w-full justify-center py-3.5 rounded-xl">
                Contact Admissions
              </button>
              <button className="btn-outline w-full justify-center py-3.5 rounded-xl">
                Live Chat Support
              </button>
            </div>
          </div>

          {/* Right: accordion */}
          <div className="lg:col-span-3 space-y-3">
            {faqs?.map((faq) => (
              <div
                key={faq?.id}
                className={`border rounded-xl overflow-hidden transition-all duration-300 ${
                  openId === faq?.id
                    ? 'border-primary/30 bg-secondary' :'border-border bg-card hover:border-border/80'
                }`}
              >
                <button
                  className="w-full flex items-center justify-between gap-4 p-5 text-left"
                  onClick={() => setOpenId(openId === faq?.id ? null : faq?.id)}
                  aria-expanded={openId === faq?.id}
                >
                  <span className="font-sans font-semibold text-sm text-foreground leading-snug">
                    {faq?.q}
                  </span>
                  <div className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${openId === faq?.id ? 'bg-primary text-white' : 'bg-secondary text-muted-foreground'}`}>
                    {openId === faq?.id ? <Minus size={14} /> : <Plus size={14} />}
                  </div>
                </button>
                <div
                  className={`transition-all duration-300 overflow-hidden ${
                    openId === faq?.id ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <p className="font-sans text-sm text-muted-foreground leading-relaxed px-5 pb-5">
                    {faq?.a}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}